import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './AdminTrainersList.css';

function AdminTrainersList() {
  const [trainers, setTrainers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSequenceMode, setIsSequenceMode] = useState(false);
  const [sequenceTrainers, setSequenceTrainers] = useState([]);
  const [isSavingSequence, setIsSavingSequence] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrainers();
  }, []);

  const fetchTrainers = async () => {
    try {
      setIsLoading(true);
      setError('');
      const { data, error: fetchError } = await supabase
        .from('trainers')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setTrainers(data || []);
    } catch (err) {
      console.error('Error fetching trainers:', err);
      setError('Failed to load training sessions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (trainerId) => {
    try {
      const { error: deleteError } = await supabase
        .from('trainers')
        .delete()
        .eq('id', trainerId);

      if (deleteError) throw deleteError;
      setTrainers(trainers.filter(t => t.id !== trainerId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting trainer:', err);
      alert(err.message || 'Failed to delete training session. Please try again.');
    }
  };

  const handleEdit = (trainerId) => {
    navigate(`/admin-dashboard/trainers/edit/${trainerId}`);
  };

  const handleSetSequence = () => {
    // Sort trainers by display_order, then by created_at
    const sorted = [...trainers].sort((a, b) => {
      if (a.display_order === null && b.display_order === null) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (a.display_order === null) return 1;
      if (b.display_order === null) return -1;
      return a.display_order - b.display_order;
    });
    setSequenceTrainers(sorted);
    setIsSequenceMode(true);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newSequence = [...sequenceTrainers];
    [newSequence[index - 1], newSequence[index]] = [newSequence[index], newSequence[index - 1]];
    setSequenceTrainers(newSequence);
  };

  const handleMoveDown = (index) => {
    if (index === sequenceTrainers.length - 1) return;
    const newSequence = [...sequenceTrainers];
    [newSequence[index], newSequence[index + 1]] = [newSequence[index + 1], newSequence[index]];
    setSequenceTrainers(newSequence);
  };

  const handleSaveSequence = async () => {
    try {
      setIsSavingSequence(true);
      
      // Update display_order for all trainers
      const updates = sequenceTrainers.map((trainer, index) => ({
        id: trainer.id,
        display_order: index + 1
      }));

      // Batch update all trainers
      for (const update of updates) {
        const { error: updateError } = await supabase
          .from('trainers')
          .update({ display_order: update.display_order })
          .eq('id', update.id);

        if (updateError) {
          throw updateError;
        }
      }

      // Refresh trainers list
      await fetchTrainers();
      setIsSequenceMode(false);
      setShowSuccessPopup(true);
      // Auto-hide after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving sequence:', err);
      alert('Failed to save sequence. Please try again.');
    } finally {
      setIsSavingSequence(false);
    }
  };

  const handleCancelSequence = () => {
    setIsSequenceMode(false);
    setSequenceTrainers([]);
  };

  const filteredTrainers = trainers.filter(trainer =>
    trainer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.specialization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.certifications?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="admin-list-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading training sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-list-container">
      <div className="list-header">
        <h1 className="list-title">Training Sessions</h1>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            className="set-sequence-btn"
            onClick={handleSetSequence}
            disabled={trainers.length === 0}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M7 12h10M11 18h6"></path>
            </svg>
            Set Sequence
          </button>
          <button className="btn-add" onClick={() => navigate('/admin-dashboard/trainers/add')}>
            + Add New Training Session
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search training sessions by name, title, specialization, or certifications..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredTrainers.length === 0 ? (
        <div className="empty-state">
          <p>{searchTerm ? 'No training sessions found matching your search.' : 'No training sessions added yet.'}</p>
          <button className="btn-add" onClick={() => navigate('/admin-dashboard/trainers/add')}>
            Add Your First Training Session
          </button>
        </div>
      ) : (
        <div className="trainers-grid">
          {filteredTrainers.map((trainer) => (
            <div key={trainer.id} className="trainer-card">
              <div className="trainer-image-container">
                <img
                  src={trainer.image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop'}
                  alt={trainer.name}
                  className="trainer-image"
                />
                {!trainer.is_active && <div className="inactive-badge">Inactive</div>}
              </div>
              <div className="trainer-content">
                <h3 className="trainer-name">{trainer.name}</h3>
                {trainer.title && <p className="trainer-title">{trainer.title}</p>}
                {trainer.specialization && (
                  <p className="trainer-specialization"><strong>Specialization:</strong> {trainer.specialization}</p>
                )}
                {trainer.certifications && (
                  <p className="trainer-certifications"><strong>Certifications:</strong> {trainer.certifications}</p>
                )}
                {trainer.experience_years && (
                  <p className="trainer-experience"><strong>Experience:</strong> {trainer.experience_years} years</p>
                )}
                {trainer.languages && Array.isArray(trainer.languages) && trainer.languages.length > 0 && (
                  <p className="trainer-languages"><strong>Languages:</strong> {trainer.languages.join(', ')}</p>
                )}
                {trainer.bio && <p className="trainer-bio">{trainer.bio.substring(0, 100)}...</p>}
                <div className="trainer-actions">
                  <button className="btn-edit" onClick={() => handleEdit(trainer.id)}>Edit</button>
                  <button className="btn-delete" onClick={() => setDeleteConfirm(trainer.id)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sequence Mode Modal */}
      {isSequenceMode && (
        <div className="sequence-modal-overlay" onClick={handleCancelSequence}>
          <div className="sequence-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sequence-modal-header">
              <h3>Set Training Session Sequence</h3>
              <p>Reorder training sessions by clicking the up/down arrows. The order will be saved to the database.</p>
            </div>
            <div className="sequence-list">
              {sequenceTrainers.map((trainer, index) => (
                <div key={trainer.id} className="sequence-item">
                  <div className="sequence-item-number">{index + 1}</div>
                  <div className="sequence-item-content">
                    <h4>{trainer.name}</h4>
                    <p className="sequence-item-meta">
                      {trainer.title || 'No title'} • {trainer.specialization || 'No specialization'}
                    </p>
                  </div>
                  <div className="sequence-item-actions">
                    <button
                      className="sequence-move-btn"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </button>
                    <button
                      className="sequence-move-btn"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === sequenceTrainers.length - 1}
                      title="Move down"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="sequence-modal-actions">
              <button 
                className="cancel-btn"
                onClick={handleCancelSequence}
                disabled={isSavingSequence}
              >
                Cancel
              </button>
              <button 
                className="save-sequence-btn"
                onClick={handleSaveSequence}
                disabled={isSavingSequence}
              >
                {isSavingSequence ? 'Saving...' : 'Save Sequence'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup-overlay" onClick={() => setShowSuccessPopup(false)}>
          <div className="success-popup" onClick={(e) => e.stopPropagation()}>
            <div className="success-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" className="success-circle"/>
                <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="success-check"/>
              </svg>
            </div>
            <h3 className="success-popup-title">Sequence Saved!</h3>
            <p className="success-popup-message">Training session order has been updated successfully.</p>
            <button 
              className="success-popup-close"
              onClick={() => setShowSuccessPopup(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this training session? This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTrainersList;
